/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';

import { Input } from '../../ux-common/src/components/ui/input';
import { System } from '../icons';
import { DetailsPageSpinner } from '../spinner/details-page-spinner';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { DropdownDataType } from './components-config-card';

interface DataDropdownProps {
  dropdownData: DropdownDataType[];
  handleSelect: (data: DropdownDataType) => void;
  placeholderText: string | undefined;
  loadingText?: string;
  loading?: boolean;
  dataSelected: DropdownDataType | null;
  isDisabled?: boolean;
  showSearch?: boolean;
  // Validation props
  required?: boolean;
  errorMessage?: string;
  showValidation?: boolean;
}

export const DataDropdown: React.FC<DataDropdownProps> = ({
  dropdownData,
  handleSelect,
  placeholderText,
  loadingText,
  loading,
  dataSelected,
  isDisabled,
  showSearch,
  required = false,
  errorMessage,
  showValidation = false,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { messages } = useLanguage();
  const generalIntl = messages?.General;

  // Validation logic
  const hasError = showValidation && required && dataSelected === null;
  const defaultErrorMessage = messages?.General?.required_field;
  const displayErrorMessage = errorMessage || defaultErrorMessage;

  const commandSelectHandler = (item: DropdownDataType) => {
    handleSelect(item);
    setIsPopoverOpen(false);
  };

  // Render loading state
  const renderLoadingState = () => (
    <div className="min-h-44 p-4 text-black">
      <DetailsPageSpinner />
    </div>
  );

  // Render dropdown items
  const renderDropdownItems = () => {
    if (!dropdownData || dropdownData.length === 0) {
      return null;
    }

    return dropdownData.map((item: DropdownDataType) => (
      <CommandItem
        key={item.id}
        onSelect={() => commandSelectHandler(item)}
        className="cursor-pointer data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130]"
        value={item.name}
      >
        <div className="cursor-pointer rounded-md px-2 py-1 hover:bg-gray-100">
          {item.name}
        </div>
      </CommandItem>
    ));
  };

  // Render command content (search + items or loading)
  const renderCommandContent = () => {
    if (loading) {
      return renderLoadingState();
    }

    return (
      <CommandList className="bg-white">
        <CommandEmpty className="h-40 pt-4 text-center text-[#323130]">
          {messages?.PromptFilters?.multi_select?.no_results_found}
        </CommandEmpty>
        <CommandGroup className="text-[#323130]">
          <ScrollArea className="max-h-40 overflow-y-auto">
            {renderDropdownItems()}
          </ScrollArea>
        </CommandGroup>
      </CommandList>
    );
  };

  return (
    <div>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              placeholder={loading ? loadingText! : placeholderText!}
              className={`h-[42px] cursor-pointer rounded-sm pr-10 ${
                hasError
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-form-control-border-rest'
              }`}
              value={dataSelected?.name || ''}
              readOnly
              disabled={isDisabled}
              aria-haspopup="listbox"
              role="combobox"
              aria-invalid={hasError}
              aria-describedby={hasError ? 'dropdown-error' : undefined}
            />
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {System.ChevronDownIcon(
                `size-5 ${hasError ? 'text-red-500' : 'text-form-input-disabled'}`
              )}
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          sideOffset={0}
          className="z-[1000] w-64 space-y-2 bg-white p-0"
          align="start"
        >
          <Command className="rounded-sm rounded-b-none bg-white">
            {showSearch && (
              <CommandInput
                placeholder={`${generalIntl?.search}...`}
                iconColor="text-form-input-disabled"
                className="text-[#323130] placeholder:text-[#8C9EA1]"
              />
            )}
            {renderCommandContent()}
          </Command>
        </PopoverContent>
      </Popover>

      {/* Error message */}
      {hasError && (
        <div
          id="dropdown-error"
          className="mt-1 text-sm text-red-500"
          role="alert"
        >
          {displayErrorMessage}
        </div>
      )}
    </div>
  );
};
